import { GRAMS_PER_OZ, GRAMS_PER_TOLA, KARATS, karatLabel, karatPurity, type Country, type Karat } from "./gold-data";

// ─────────────────────────────────────────────────────────────────────────────
// Long-Tail Matrix Engine
// ─────────────────────────────────────────────────────────────────────────────

export const UNITS = [
  "1-gram",
  "10-grams",
  "1-tola",
  "1-ounce",
  "1-masha",
  "1-ratti",
  "1-sovereign",
] as const;
export type UnitSlug = (typeof UNITS)[number];

export const MODIFIERS = ["today", "tomorrow-forecast", "weekly-trend", "history-price"] as const;
export type ModifierSlug = (typeof MODIFIERS)[number];

// Grams per 1 of each unit
export const GRAMS_PER_UNIT: Record<UnitSlug, number> = {
  "1-gram": 1,
  "10-grams": 10,
  "1-tola": GRAMS_PER_TOLA, // 11.6638
  "1-ounce": GRAMS_PER_OZ, // 31.1035
  "1-masha": 0.972,
  "1-ratti": 0.121,
  "1-sovereign": 8, // Pavan
};

export const UNIT_LABEL: Record<UnitSlug, string> = {
  "1-gram": "1 Gram",
  "10-grams": "10 Grams",
  "1-tola": "1 Tola",
  "1-ounce": "1 Ounce",
  "1-masha": "1 Masha",
  "1-ratti": "1 Ratti",
  "1-sovereign": "1 Sovereign (Pavan)",
};

export const MODIFIER_LABEL: Record<ModifierSlug, string> = {
  today: "Today",
  "tomorrow-forecast": "Tomorrow Forecast",
  "weekly-trend": "Weekly Trend",
  "history-price": "Price History",
};

export const COUNTRY_ALIAS: Record<string, Country> = {
  pk: "pakistan",
  pakistan: "pakistan",
  in: "india",
  india: "india",
};

export function parseUnit(raw: string): UnitSlug | null {
  const s = raw.toLowerCase();
  return (UNITS as readonly string[]).includes(s) ? (s as UnitSlug) : null;
}
export function parseKarat(raw: string): Karat | null {
  const s = raw.toLowerCase();
  return (KARATS as readonly string[]).includes(s) ? (s as Karat) : null;
}
export function parseModifier(raw: string): ModifierSlug | null {
  const s = raw.toLowerCase();
  return (MODIFIERS as readonly string[]).includes(s) ? (s as ModifierSlug) : null;
}

/**
 * Long-tail price = (24K per-gram base) × purity × grams in the requested unit.
 */
export function computeLongTailPrice(args: {
  perGram24: number;
  karat: Karat;
  unit: UnitSlug;
}): number {
  return args.perGram24 * karatPurity(args.karat) * GRAMS_PER_UNIT[args.unit];
}

export function longTailIntroParagraph(args: {
  karat: Karat;
  cityName: string;
  unit: UnitSlug;
  modifier: ModifierSlug;
  priceFormatted: string;
  currencyCode: "PKR" | "INR";
}): string {
  const k = karatLabel(args.karat);
  const u = UNIT_LABEL[args.unit];
  const m = MODIFIER_LABEL[args.modifier];
  return (
    `Get the latest updates on ${k} gold price in ${args.cityName} per ${u} for ${m}. ` +
    `Currently, ${k} gold is trading at ${args.priceFormatted} (${args.currencyCode}). ` +
    `If you are planning to buy jewelry or invest in bullion in the ${args.cityName} local market, ` +
    `tracking the daily price per ${u} helps you avoid overpaying making charges and wastage premiums.`
  );
}

/** Forecast next-day move from the last 3 daily points (24K base). */
export function forecastTomorrow(args: {
  history: { date: string; price24k: number }[];
  todayPrice: number;
}): { trendPct: number; direction: "up" | "down" | "flat"; resistance: number; support: number } {
  const pts = args.history.slice(-3);
  let trendPct = 0;
  if (pts.length >= 2) {
    const first = pts[0].price24k;
    const last = pts[pts.length - 1].price24k;
    if (first > 0) trendPct = ((last - first) / first) * 100;
  }
  const direction = trendPct > 0.05 ? "up" : trendPct < -0.05 ? "down" : "flat";
  const resistance = args.todayPrice * 1.001;
  const support = args.todayPrice * 0.999;
  return { trendPct, direction, resistance, support };
}

/** 6 related internal links for the footer crumb. Stable, deterministic. */
export function relatedLongTailLinks(args: { country: Country; citySlug: string }) {
  const c = args.country === "pakistan" ? "pk" : "in";
  const picks: Array<{ karat: Karat; unit: UnitSlug; modifier: ModifierSlug; label: (cityName: string) => string }> = [
    { karat: "18k", unit: "1-gram", modifier: "today", label: (n) => `Check 18K gold rate in ${n}` },
    { karat: "22k", unit: "1-tola", modifier: "today", label: (n) => `22K per tola in ${n} today` },
    { karat: "24k", unit: "1-masha", modifier: "today", label: (n) => `1 Masha 24K gold price in ${n}` },
    { karat: "24k", unit: "1-sovereign", modifier: "today", label: (n) => `1 Sovereign (Pavan) gold price in ${n}` },
    { karat: "22k", unit: "1-gram", modifier: "tomorrow-forecast", label: (n) => `Tomorrow's 22K gold forecast in ${n}` },
    { karat: "24k", unit: "10-grams", modifier: "weekly-trend", label: (n) => `Weekly 24K gold trend in ${n}` },
  ];
  return picks.map((p) => ({
    href: `/${c}/${args.citySlug}/${p.karat}/${p.unit}/${p.modifier}`,
    karat: p.karat,
    unit: p.unit,
    modifier: p.modifier,
    label: p.label,
  }));
}